import type { PlayerHistoryResponse } from '@/api/generated/models'
import type { RatingPoint, Transaction } from '../types'
import { mapRatings } from './ratings'
import { transactionText } from '@/features/player/history/transactionText'

export function mapHistory(api: PlayerHistoryResponse): {
  ratingsHistory: RatingPoint[]
  transactions: Transaction[]
} {
  const ratingsHistory: RatingPoint[] = (api.ratingsHistory ?? []).map(row => {
    const asOf =
      row.year != null
        ? String(row.year)
        : row.age != null
          ? `Age ${row.age}`
          : ''

    return {
      asOf,
      year: row.year,
      age: row.age,
      position: row.posnShort ?? null,
      salary: row.salary,
      attrs: mapRatings(row.ratings),
    }
  })

  // Sort chronologically ascending by AGE (youngest → current). This MUST key on
  // the numeric age, NOT the display `asOf` string: asOf is a year, or "Age N"
  // when a season has no year, and a lexicographic sort ranks "Age N" ABOVE real
  // year strings (letters sort after digits) — which pinned the earliest
  // (yearless) season to the very top ("Age 17, then 34, 33, 32…"). Every
  // history row carries an age, so age is the reliable ordering key.
  ratingsHistory.sort(
    (a, b) => (a.age ?? Number.POSITIVE_INFINITY) - (b.age ?? Number.POSITIVE_INFINITY),
  )

  // Use the same human-readable description the full History page renders
  // (transactionText: the legacy Type 1..11 switch). The year (from the row's
  // date) becomes the season prefix. Rows that resolve to empty text — Type 4
  // trades with no tradeId, Type 10 no-op moves — are dropped.
  const transactions: Transaction[] = (api.transactions ?? [])
    .map(t => ({
      season: t.date ? Number(t.date.slice(0, 4)) || undefined : undefined,
      label: transactionText(t),
    }))
    .filter(t => t.label !== '')

  return { ratingsHistory, transactions }
}
